import React, { useState, useEffect } from "react";
import { Grid, Button, TextField, Typography, Alert, Box, MenuItem } from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
import { checkEquipmentExists, registerEquipment } from "../services/userEquipmentService";
import EquipmentUsersModal from "./EquipmentUsersModal";
import CameraAltIcon from "@mui/icons-material/CameraAlt";

const EquipmentRegistration = ({
  cardUserId,
  onSuccess,
  onCancel,
  skipInitialQuestion = false,
}) => {
  const [step, setStep] = useState(skipInitialQuestion ? 2 : 1);
  const [wantsToRegister, setWantsToRegister] = useState(null);
  const [formData, setFormData] = useState({
    type: "",
    brand: "",
    model: "",
    serial_number: "",
    color: "",
    description: "",
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [showImageModal, setShowImageModal] = useState(false);
  const [checkResult, setCheckResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showUsersModal, setShowUsersModal] = useState(false);

  const equipmentTypes = [
    { value: "computador", label: "Computador" },
    { value: "tableta", label: "Tableta" },
    { value: "otros", label: "Otros" },
  ];

  const handleInitialChoice = (choice) => {
    setWantsToRegister(choice);
    if (choice === "no") {
      onCancel();
    } else {
      setStep(2);
    }
  };

  const checkExists = async () => {
    try {
      setLoading(true);
      const result = await checkEquipmentExists(cardUserId, formData.serial_number.trim());
      setCheckResult(result);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (formData.serial_number.length > 3 || formData.serial_number === "") {
      const timer = setTimeout(checkExists, 500);
      return () => clearTimeout(timer);
    } else {
      setCheckResult(null);
    }
  }, [formData.serial_number]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleRegister = async () => {
    try {
      setLoading(true);
      setError("");

      const equipmentData = {
        card_user_id: cardUserId,
        ...formData,
      };

      const result = await registerEquipment(equipmentData, imageFile);

      if (result.success) {
        if (result.data.used_document_as_serial) {
          alert("Equipo registrado usando tu número de cédula como serial");
        }
        onSuccess(result.data);
      } else {
        setError(result.message || "Error al registrar equipo");
      }
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  if (step === 1 && !skipInitialQuestion) {
    return (
      <MDBox p={3}>
        <MDTypography variant="h5" mb={3}>
          ¿Trae algún equipo electrónico?
        </MDTypography>
        <Grid container spacing={3} justifyContent="center">
          <Grid item xs={12} md={6}>
            <MDButton
              variant="outlined"
              color="secondary"
              fullWidth
              onClick={() => handleInitialChoice("no")}
            >
              No
            </MDButton>
          </Grid>
          <Grid item xs={12} md={6}>
            <MDButton
              variant="gradient"
              color="info"
              fullWidth
              onClick={() => handleInitialChoice("yes")}
            >
              Sí, quiero registrarlo
            </MDButton>
          </Grid>
        </Grid>
      </MDBox>
    );
  }

  return (
    <MDBox p={2}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <TextField
            select
            label="Tipo de equipo"
            value={formData.type}
            onChange={(e) => handleInputChange("type", e.target.value)}
            fullWidth
            required
            sx={{
              "& .MuiInputBase-root": {
                height: "45px",
              },
              "& .MuiSelect-select": {
                padding: "12px 14px",
              },
            }}
          >
            {equipmentTypes.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item xs={12} md={6}>
          <MDInput
            label="Marca"
            placeholder="Ej: Dell, HP, Apple"
            value={formData.brand}
            onChange={(e) => handleInputChange("brand", e.target.value)}
            fullWidth
            required
            inputProps={{ maxLength: 100 }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <MDInput
            label="Modelo"
            placeholder="Ej: Inspiron 15 3000"
            value={formData.model}
            onChange={(e) => handleInputChange("model", e.target.value)}
            fullWidth
            required
            inputProps={{ maxLength: 100 }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <MDInput
            label="Número de Serie"
            placeholder="Ej: DL123456789"
            value={formData.serial_number}
            onChange={(e) => handleInputChange("serial_number", e.target.value)}
            fullWidth
            inputProps={{ maxLength: 100 }}
          />
          <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 0.5 }}>
            Si no tienes número de serie, déjalo vacío y se usará tu cédula
          </Typography>
        </Grid>

        <Grid item xs={12} md={6}>
          <MDInput
            label="Color"
            placeholder="Ej: Negro, Gris, Azul"
            value={formData.color}
            onChange={(e) => handleInputChange("color", e.target.value)}
            fullWidth
            required
            inputProps={{ maxLength: 50 }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <MDInput
            label="Descripción"
            placeholder="Descripción adicional del equipo (opcional)"
            value={formData.description}
            onChange={(e) => handleInputChange("description", e.target.value)}
            fullWidth
            inputProps={{ maxLength: 500 }}
          />
        </Grid>

        {(formData.serial_number.length > 3 || formData.serial_number === "") && (
          <Grid item xs={12}>
            {loading ? (
              <Alert severity="info">Verificando disponibilidad...</Alert>
            ) : checkResult ? (
              checkResult.user_has_equipment && formData.serial_number !== "" ? (
                <Alert severity="warning">Ya tienes este equipo registrado anteriormente</Alert>
              ) : checkResult.other_users_count > 0 ? (
                <Alert
                  severity="info"
                  action={
                    <Button color="inherit" size="small" onClick={() => setShowUsersModal(true)}>
                      Ver quién lo tiene
                    </Button>
                  }
                >
                  {formData.serial_number === ""
                    ? `Tienes ${checkResult.other_users_count} equipo(s) registrado(s) con tu cédula, puedes registrar otro`
                    : `Este equipo ya está registrado por ${checkResult.other_users_count} persona(s), pero tú también puedes registrarlo`}
                </Alert>
              ) : (
                <Alert severity="success">
                  {formData.serial_number === ""
                    ? "Se usará tu número de cédula como serial en caso de que no tenga uno"
                    : "Equipo disponible para registro"}
                </Alert>
              )
            ) : null}
          </Grid>
        )}

        <Grid item xs={12}>
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <input
                type="file"
                accept="image/jpeg,image/png,image/jpg"
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file && file.size <= 2 * 1024 * 1024) {
                    setImageFile(file);
                    const reader = new FileReader();
                    reader.onload = (e) => setImagePreview(e.target.result);
                    reader.readAsDataURL(file);
                  } else {
                    alert("La imagen debe ser menor a 2MB");
                    e.target.value = "";
                  }
                }}
                style={{ flex: 1, minWidth: 0, width: "100%" }}
                required
                key={imageFile ? imageFile.name : "no-file"}
              />
              <Box
                sx={{
                  bgcolor: "white",
                  borderRadius: "50%",
                  p: 0.6,
                  boxShadow: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  width: "40px",
                  height: "40px",
                  flexShrink: 0,
                }}
                onClick={async () => {
                  try {
                    const stream = await navigator.mediaDevices.getUserMedia({
                      video: { facingMode: "environment" },
                    });

                    const video = document.createElement("video");
                    video.srcObject = stream;
                    video.play();

                    const modal = document.createElement("div");
                    modal.style.cssText =
                      "position:fixed;top:0;left:0;width:100%;height:100%;background:rgba(0,0,0,0.9);z-index:9999;display:flex;align-items:center;justify-content:center;flex-direction:column";

                    video.style.cssText = "max-width:90%;max-height:70%;border-radius:8px";

                    const captureBtn = document.createElement("button");
                    captureBtn.textContent = "Capturar";
                    captureBtn.style.cssText =
                      "margin:10px;padding:10px 20px;background:#1976d2;color:white;border:none;border-radius:5px;cursor:pointer;font-size:16px";

                    const closeBtn = document.createElement("button");
                    closeBtn.textContent = "Cerrar";
                    closeBtn.style.cssText =
                      "margin:10px;padding:10px 20px;background:#f44336;color:white;border:none;border-radius:5px;cursor:pointer;font-size:16px";

                    modal.appendChild(video);
                    modal.appendChild(captureBtn);
                    modal.appendChild(closeBtn);
                    document.body.appendChild(modal);

                    captureBtn.onclick = () => {
                      const canvas = document.createElement("canvas");
                      canvas.width = video.videoWidth;
                      canvas.height = video.videoHeight;
                      canvas.getContext("2d").drawImage(video, 0, 0);

                      canvas.toBlob(
                        (blob) => {
                          const file = new File([blob], "camera-capture.jpg", {
                            type: "image/jpeg",
                          });
                          setImageFile(file);
                          const reader = new FileReader();
                          reader.onload = (e) => setImagePreview(e.target.result);
                          reader.readAsDataURL(file);
                          stream.getTracks().forEach((track) => track.stop());
                          document.body.removeChild(modal);
                        },
                        "image/jpeg",
                        0.8
                      );
                    };

                    closeBtn.onclick = () => {
                      stream.getTracks().forEach((track) => track.stop());
                      document.body.removeChild(modal);
                    };
                  } catch (error) {
                    const input = document.createElement("input");
                    input.type = "file";
                    input.accept = "image/*";
                    input.capture = "environment";
                    input.onchange = (e) => {
                      const file = e.target.files[0];
                      if (file) {
                        setImageFile(file);
                        const reader = new FileReader();
                        reader.onload = (e) => setImagePreview(e.target.result);
                        reader.readAsDataURL(file);
                      }
                    };
                    input.click();
                  }
                }}
                title="Tomar foto con cámara"
              >
                <CameraAltIcon sx={{ color: "#1976d2", fontSize: 28 }} />
              </Box>
            </Box>
          </Box>
          <Typography variant="caption" color="textSecondary" display="block">
            Foto del equipo * (obligatoria) - Máximo 2MB
          </Typography>
          {imageFile && (
            <>
              <Typography variant="caption" color="success.main" display="block" sx={{ mt: 1 }}>
                ✓ Imagen seleccionada: {imageFile.name}
              </Typography>
              {imagePreview && (
                <Box sx={{ mt: 1, textAlign: "center" }}>
                  <img
                    src={imagePreview}
                    alt="Preview"
                    style={{
                      maxWidth: "150px",
                      maxHeight: "100px",
                      borderRadius: "4px",
                      border: "1px solid #e0e0e0",
                      objectFit: "cover",
                      cursor: "pointer",
                    }}
                    onClick={() => setShowImageModal(true)}
                    title="Click para ver imagen completa"
                  />
                </Box>
              )}
            </>
          )}
        </Grid>
        <Grid item xs={12}>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: "space-between",
              gap: 2,
              mt: 2,
            }}
          >
            <MDButton
              variant="outlined"
              color="secondary"
              onClick={onCancel}
              sx={{ order: { xs: 2, sm: 1 } }}
            >
              Cancelar
            </MDButton>
            <MDButton
              variant="gradient"
              color="info"
              onClick={handleRegister}
              disabled={
                loading ||
                !formData.type ||
                !formData.brand ||
                !formData.model ||
                !formData.color ||
                !imageFile ||
                (checkResult && checkResult.user_has_equipment && formData.serial_number !== "")
              }
              sx={{ order: { xs: 1, sm: 2 } }}
            >
              {loading ? "Registrando..." : "Registrar Equipo"}
            </MDButton>
          </Box>
        </Grid>
      </Grid>

      <EquipmentUsersModal
        open={showUsersModal}
        onClose={() => setShowUsersModal(false)}
        serialNumber={formData.serial_number}
      />

      {/* Modal para ver imagen completa */}
      {showImageModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0,0,0,0.9)",
            zIndex: 10000,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
          }}
          onClick={() => setShowImageModal(false)}
        >
          <button
            style={{
              position: "absolute",
              top: "10px",
              right: "10px",
              background: "rgba(255,255,255,0.9)",
              border: "none",
              borderRadius: "50%",
              width: "35px",
              height: "35px",
              fontSize: "18px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 10001,
            }}
            onClick={(e) => {
              e.stopPropagation();
              setShowImageModal(false);
            }}
          >
            ×
          </button>
          <img
            src={imagePreview}
            alt="Imagen completa"
            style={{
              maxWidth: "85%",
              maxHeight: "70%",
              objectFit: "contain",
            }}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </MDBox>
  );
};

export default EquipmentRegistration;
